import { Link } from "react-router-dom";
import { FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { EmailContactTypeahead } from "@/pages/admin/portal/components/EmailContactTypeahead";
import type { PortalContactSearchResultDto } from "@/api/portalAccounts";
import {
  MATERIAL_AUDIENCES,
  type MaterialAudience,
} from "@/api/portalMaterials";

// WS2 — Step 1 "Who" of the Send Signup Link wizard. The email field
// reuses the already-shipping EmailContactTypeahead, falling back to a
// locked Input when ?email= is present. The block-on-existing-account
// banner renders below the form so it sits next to the wizard footer.

const ACCOUNT_TYPES: MaterialAudience[] = MATERIAL_AUDIENCES.filter(
  (a) => a !== "All",
);

export interface Step1ContactProps {
  email: string;
  fullName: string;
  accountType: MaterialAudience | "";
  emailError: string | undefined;
  lockedEmail: string;
  lockedAccountType: MaterialAudience | null;
  existingAccountId: string | null;
  onEmailChange: (next: string) => void;
  onFullNameChange: (next: string) => void;
  onAccountTypeChange: (next: MaterialAudience | "") => void;
  onSelectContact: (r: PortalContactSearchResultDto) => void;
  onEmailBlur: (typed: string) => void;
}

export function Step1Contact({
  email,
  fullName,
  accountType,
  emailError,
  lockedEmail,
  lockedAccountType,
  existingAccountId,
  onEmailChange,
  onFullNameChange,
  onAccountTypeChange,
  onSelectContact,
  onEmailBlur,
}: Step1ContactProps) {
  return (
    <div className="flex flex-col gap-space-3">
      <p className="text-small text-gray-mid m-0 max-w-reading">
        Set up portal access for an external party — broker, buyer, seller,
        or vendor — to receive collection requests and view their materials.
      </p>

      <section className="flex flex-col gap-space-2">
        <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow">
          Contact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
          <FormField label="Email" required error={emailError}>
            {lockedEmail ? (
              <Input
                type="email"
                value={email}
                invalid={!!emailError}
                onChange={(e) => onEmailChange(e.target.value)}
                disabled
              />
            ) : (
              <EmailContactTypeahead
                value={email}
                onEmailChange={onEmailChange}
                onSelectContact={onSelectContact}
                onEmailBlur={onEmailBlur}
                invalid={!!emailError}
                placeholder="Search by name or email…"
              />
            )}
          </FormField>
          <FormField label="Full name" required>
            <Input
              value={fullName}
              onChange={(e) => onFullNameChange(e.target.value)}
            />
          </FormField>
          <FormField label="Party type" required>
            <Select
              value={accountType}
              onChange={(e) =>
                onAccountTypeChange(e.target.value as MaterialAudience | "")
              }
              disabled={!!lockedAccountType}
            >
              <option value="">Select…</option>
              {ACCOUNT_TYPES.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </FormField>
        </div>
      </section>

      {existingAccountId && (
        <p
          className="text-small text-status-error m-0"
          role="alert"
          data-testid="existing-account-block"
        >
          This email already has an active portal account.{" "}
          <Link
            to={`/admin/portal-accounts/${existingAccountId}`}
            className="underline"
          >
            Open the account
          </Link>{" "}
          to add more bundles.
        </p>
      )}
    </div>
  );
}
